import { Component, OnInit } from "@angular/core";
import {
  AbstractControl,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from "@angular/forms";
import { Router } from "@angular/router";
import { NGXLogger } from "ngx-logger";
import { PadlockService } from "../../padlock/padlock.service";
import { ToastService } from "../../shared/toast.service";
import {
  buildForcePasswordCompletionMarker,
  FORCE_PASSWORD_CHANGE_COMPLETED_USER_KEY,
} from "../../auth/temporary-password.guard";

const passwordMatchValidator: ValidatorFn = (
  control: AbstractControl,
): ValidationErrors | null => {
  const formGroup = control as UntypedFormGroup;
  return formGroup.get("newPassword")?.value ===
    formGroup.get("confirmNewPassword")?.value
    ? null
    : { passwordMismatch: true };
};

@Component({
  selector: "app-force-password-change",
  templateUrl: "./force-password-change.component.html",
  styleUrls: ["./force-password-change.component.scss"],
  standalone: false,
})
export class ForcePasswordChangeComponent implements OnInit {
  userId: string | null = null;
  completionMarker: string | null = null;
  submitting = false;

  hideCurrentPassword = true;
  hideNewPassword = true;
  hideConfirmNewPassword = true;

  form = new UntypedFormGroup(
    {
      currentPassword: new UntypedFormControl("", [Validators.required]),
      newPassword: new UntypedFormControl("", [Validators.required]),
      confirmNewPassword: new UntypedFormControl("", [Validators.required]),
    },
    { validators: [passwordMatchValidator] },
  );

  constructor(
    private padlockService: PadlockService,
    private toast: ToastService,
    private router: Router,
    private logger: NGXLogger,
  ) {}

  ngOnInit(): void {
    const decoded = this.padlockService.getDecodedToken();
    this.userId = decoded?.sub || null;
    this.completionMarker = this.userId
      ? buildForcePasswordCompletionMarker(this.userId, decoded)
      : null;
  }

  submit(): void {
    if (!this.form.valid || !this.userId || this.submitting) {
      return;
    }

    const currentPassword = this.form.get("currentPassword")?.value;
    const newPassword = this.form.get("newPassword")?.value;

    this.submitting = true;

    this.padlockService
      .changePassword(this.userId, currentPassword, newPassword)
      .subscribe({
        next: () => {
          if (this.completionMarker) {
            sessionStorage.setItem(
              FORCE_PASSWORD_CHANGE_COMPLETED_USER_KEY,
              this.completionMarker,
            );
          }
          this.toast.openToast("Password updated successfully");
          this.form.reset();
          this.submitting = false;
          this.router.navigateByUrl("/");
        },
        error: (error) => {
          this.logger.error(
            "FORCE PASSWORD CHANGE: failed to change password",
            error,
          );
          this.toast.openErrorToast("Unable to update password");
          this.submitting = false;
        },
      });
  }

  passwordsDoNotMatch(): boolean {
    return !!(
      this.form.hasError("passwordMismatch") &&
      this.form.get("confirmNewPassword")?.touched
    );
  }
}
